﻿import React, { useState } from 'react';
import { Sun, Moon, Info, Home, LogOut, User as UserIcon, LayoutDashboard, Menu, X, Crown, Shield, Clock, BookOpen, Award, Mail } from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import { StreakBadge } from './ui/StreakBadge';
import { SubscriptionDetailsModal } from './ui/SubscriptionDetailsModal';
import { Logo } from './Logo';

interface HeaderProps {
  isDark: boolean;
  onThemeToggle: () => void;
  currentView: string;
  onNavigate: (view: string) => void;
  user: User | null;
  onSignOut: () => void;
  onUpgradeClick?: () => void;
  streakCount?: number;
  isPro?: boolean;
  hasPendingRequest?: boolean;
  proExpiresAt?: string | null;
  onSubscriptionRefresh?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ isDark, onThemeToggle, currentView, onNavigate, user, onSignOut, onUpgradeClick, streakCount, isPro, hasPendingRequest, proExpiresAt, onSubscriptionRefresh }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  
  const handleNavigate = (view: string) => {
    onNavigate(view);
    setSidebarOpen(false);
  };

  return (
    <>
      {/* Top Header */}
      <header className={`sticky top-0 z-50 w-screen ${isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'} border-b backdrop-blur-md`}>
        <div className="px-3 sm:px-4 md:px-6 lg:px-8 py-3 md:py-4 flex items-center justify-between gap-3 md:gap-4">
          {/* Mobile Hamburger Menu */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`md:hidden p-2 rounded-lg transition-all ${
              isDark ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-zinc-100 text-zinc-600'
            }`}
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Logo - Centered on mobile, left on desktop */}
          <button
            onClick={() => handleNavigate('home')}
            className="group cursor-pointer flex items-center justify-center flex-1 md:flex-none md:justify-start gap-1 sm:gap-1.5 md:gap-3 min-w-0"
          >
            {/* Psychological SVG Logo */}
            <div className="group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300 flex-shrink-0">
              <Logo size="sm" animated={true} />
            </div>
            {/* Logo Text */}
            <h1 className="font-tech font-bold text-lg sm:text-xl md:text-2xl lg:text-3xl tracking-tight whitespace-nowrap truncate" style={{ letterSpacing: '-0.02em' }}>
              <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-red-600 bg-clip-text text-transparent transition-all group-hover:scale-105 inline-block font-bold drop-shadow-lg">NanoLez</span>

            </h1>
          </button>

          {/* Mobile: Profile button (top right) */}
          {user && (
            <button
              onClick={() => handleNavigate('profile')}
              className={`md:hidden w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-red-500 to-rose-500 text-white flex items-center justify-center font-bold text-xs hover:ring-2 hover:ring-offset-2 transition-all flex-shrink-0 ${
                isDark ? 'hover:ring-red-500 hover:ring-offset-zinc-950' : 'hover:ring-red-500 hover:ring-offset-white'
              }`}
              title="View Profile"
            >
              {user.user_metadata?.avatar_url ? (
                <img src={user.user_metadata.avatar_url} alt={user.user_metadata?.full_name || 'Profile'} className="w-full h-full object-cover" />
              ) : (
                <span>{(user.user_metadata?.full_name || user.email || 'U')[0]?.toUpperCase()}</span>
              )}
            </button>
          )}

          {/* Desktop Navigation - Hidden on mobile */}
          <nav className="hidden md:flex items-center gap-1">
            { [
              ...(!user ? [{ id: 'home', label: 'Home', icon: Home }] : []),
              ...(user ? [{ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard }] : []),
              ...(user ? [{ id: 'exams', label: 'Exams', icon: Award }] : []),
              { id: 'contact', label: 'Contact', icon: Mail },
              ...(user && user.email === 'dynamicnabid@gmail.com' ? [{ id: 'inbox', label: 'Inbox', icon: Mail }] : []),
              ...(user && user.email === 'dynamicnabid@gmail.com' ? [{ id: 'admin', label: 'Admin', icon: Shield }] : [])
            ].map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigate(item.id)}
                  className={`flex items-center gap-2 px-3 lg:px-4 py-2 rounded-lg transition-all text-sm ${
                    isActive
                      ? isDark
                        ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                        : 'bg-red-100 text-red-600 border border-red-300'
                      : isDark
                      ? 'text-zinc-300 hover:bg-zinc-800'
                      : 'text-zinc-600 hover:bg-zinc-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Desktop: Theme toggle + Streak + Login/Profile (top right corner) */}
          <div className="hidden md:flex items-center gap-3">
            { streakCount !== undefined && streakCount > 0 && user && (
              <StreakBadge count={streakCount} isDark={isDark} className="mr-1" />
            )}
            
            {user && !isPro && !hasPendingRequest && (
              <button
                onClick={() => onUpgradeClick?.()}
                className={`px-4 py-2 ${
                  streakCount && streakCount >= 14
                    ? 'bg-gradient-to-r from-red-500 to-orange-600 hover:shadow-red-500/30 border-red-400/50'
                    : 'bg-gradient-to-r from-amber-400 to-orange-500 border-amber-300/50 hover:shadow-orange-500/30'
                } text-white rounded-lg font-bold text-sm hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 border`}
              >
                {streakCount && streakCount >= 20 ? (
                  <>
                    <span className="text-lg">🔥</span>
                    <span>Own {streakCount} Days</span>
                  </>
                ) : streakCount && streakCount >= 14 ? (
                  <>
                    <span>🔥</span>
                    <span>Guard Your Identity</span>
                  </>
                ) : streakCount && streakCount >= 10 ? (
                  <>
                    <span>⚡</span>
                    <span>Protect Momentum</span>
                  </>
                ) : (
                  <>
                    <Crown className="w-4 h-4 fill-current" />
                    <span>Unlock Potential</span>
                  </>
                )}
              </button>
            )}

            {user && !isPro && hasPendingRequest && (
              <button
                onClick={() => onUpgradeClick?.()}
                className="px-4 py-2 bg-zinc-500/10 text-zinc-500 rounded-lg font-bold text-sm flex items-center gap-2 border border-zinc-500/20 hover:bg-zinc-500/20 transition-all"
              >
                <Clock className="w-4 h-4" />
                PENDING
              </button>
            )}

            {user && isPro && (
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className="px-3 py-1.5 bg-emerald-500/10 text-emerald-500 rounded-lg font-bold text-xs flex items-center gap-1.5 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all shadow-sm"
              >
                <Crown className="w-3.5 h-3.5 fill-current" />
                PRO
              </button>
            )}

            <button
              onClick={onThemeToggle}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-zinc-800 text-zinc-300' : 'hover:bg-zinc-200 text-zinc-700'
              }`}
            >
              {isDark ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>

            {user ? (
              <button
                onClick={() => handleNavigate('profile')}
                className={`w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-red-500 to-rose-500 text-white flex items-center justify-center font-bold text-sm hover:ring-2 hover:ring-offset-2 transition-all ${
                  isDark ? 'hover:ring-red-500 hover:ring-offset-zinc-900' : 'hover:ring-red-500 hover:ring-offset-white'
                }`}
                title="View Profile"
              >
                {user.user_metadata?.avatar_url ? (
                  <img src={user.user_metadata.avatar_url} alt={user.user_metadata?.full_name || 'Profile'} className="w-full h-full object-cover" />
                ) : (
                  <span>{(user.user_metadata?.full_name || user.email || 'N')[0]?.toUpperCase()}</span>
                )}
              </button>
            ) : (
              <button
                onClick={() => handleNavigate('login')}
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-lg font-medium text-sm hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2"
              >
                <UserIcon className="w-4 h-4" />
                Login
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar Overlay - Mobile Navigation */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation - Mobile */}
      <aside
        className={`fixed left-0 top-0 h-screen w-64 z-50 transform transition-transform duration-300 ease-out md:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'} border-r`}
      >
        <div className="h-full flex flex-col p-4">
          {/* Sidebar Header with Close Button */}
          <div className="flex items-center justify-between mb-6 pt-2">
            <span className="text-lg font-black bg-gradient-to-r from-amber-400 via-orange-500 to-red-600 bg-clip-text text-transparent uppercase tracking-wider">
              NanoLez
            </span>
            <button
              onClick={() => setSidebarOpen(false)}
              className={`p-2 rounded-lg transition-all ${
                isDark ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-zinc-100 text-zinc-600'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Profile Section */}
          {user && (
            <div className={`p-3 rounded-lg mb-6 ${isDark ? 'bg-zinc-900' : 'bg-zinc-50'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-red-500 to-rose-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0`}>
                  {user.user_metadata?.avatar_url ? (
                    <img src={user.user_metadata.avatar_url} alt={user.user_metadata?.full_name || 'Profile'} className="w-full h-full object-cover" />
                  ) : (
                    <span>{(user.user_metadata?.full_name || user.email || 'U')[0]?.toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`font-semibold text-sm ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                      {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
                    </p>
                    {isPro && (
                      <button
                        onClick={() => {
                          setShowSubscriptionModal(true);
                          setSidebarOpen(false);
                        }}
                        className="px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-500 text-[10px] font-bold border border-emerald-500/20 hover:bg-emerald-500/20 transition-all cursor-pointer"
                        title="View subscription details"
                      >
                        PRO
                      </button>
                    )}
                  </div>
                  <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'} truncate`}>
                    {user.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <nav className="flex-1 space-y-2">
            {[
              { id: 'home', label: 'Home', icon: Home },
              ...(user ? [{ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard }] : []),
              ...(user ? [{ id: 'exams', label: 'Exams', icon: Award }] : []),
              { id: 'contact', label: 'Contact', icon: Mail },
              ...(user && user.email === 'dynamicnabid@gmail.com' ? [{ id: 'admin', label: 'Admin', icon: Shield }] : []),
            ].map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigate(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-medium text-sm ${
                    isActive
                      ? isDark
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-red-100 text-red-600'
                      : isDark
                      ? 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100'
                      : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}            
            {/* Login button for unauthenticated users in mobile sidebar */}
            {!user && (
              <button
                onClick={() => handleNavigate('login')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-medium text-sm bg-gradient-to-r from-red-500 to-rose-500 text-white hover:shadow-lg"
              >
                <UserIcon className="w-5 h-5" />
                <span>Login</span>
              </button>
            )}

            {/* Pro CTA for free users in mobile sidebar */}
            {user && !isPro && !hasPendingRequest && (
              <button
                onClick={() => {
                  onUpgradeClick?.();
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-bold text-sm bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:shadow-lg mt-4"
              >
                <Crown className="w-5 h-5 fill-current" />
                <span>Upgrade to PRO</span>
              </button>
            )}
          </nav>

          {/* Sidebar Footer */}
          <div className="border-t pt-4 mt-4 space-y-2" style={{ borderTopColor: isDark ? '#27272a' : '#e4e4e7' }}>
            {/* Subscription Status for Pro Users */}
            {user && isPro && (
              <button
                onClick={() => {
                  setShowSubscriptionModal(true);
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-medium text-sm bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 hover:bg-emerald-500/20"
              >
                <Crown className="w-5 h-5 fill-current" />
                <span>View Pro Details</span>
              </button>
            )}

            {/* Pending Request Status */}
            {user && !isPro && hasPendingRequest && (
              <button
                onClick={() => {
                  setShowSubscriptionModal(true);
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-medium text-sm bg-zinc-500/10 text-zinc-500 border border-zinc-500/20 hover:bg-zinc-500/20"
              >
                <Clock className="w-5 h-5" />
                <span>Upgrade Pending</span>
              </button>
            )}

            {/* Theme Toggle */}
            <button
              onClick={() => {
                onThemeToggle();
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-medium text-sm ${
                isDark
                  ? 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100'
                  : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
              }`}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Subscription Details Modal */}
      <SubscriptionDetailsModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        isDark={isDark}
        userEmail={user?.email || undefined}
        proExpiresAt={proExpiresAt}
        onRefresh={onSubscriptionRefresh}
        isPro={isPro}
      />
    </>
  );
};
